import { el, svg } from "../core/dom";
import type { QuickInsights } from "../api/types";
import type { ResolvedBrand } from "./colors";
import type { Fields, FieldErrors } from "./validation";

type IdleHandlers = {
  onInput: (field: keyof Fields, value: string) => void;
  onBlur: (field: keyof Fields) => void;
  onSubmit: () => void;
};

export type IdleRefs = {
  root: HTMLElement;
  fieldInputs: Record<keyof Fields, HTMLInputElement>;
  fieldErrors: Record<keyof Fields, HTMLElement>;
  submitBtn: HTMLButtonElement;
  generalError: HTMLElement;
};

function header(brand: ResolvedBrand): HTMLElement {
  const logoImg = el("img", {
    src: brand.logo,
    alt: `${brand.name} logo`,
    className: "dpdpa-logo",
    onerror: (ev: Event) => {
      const img = ev.currentTarget as HTMLImageElement;
      img.style.display = "none";
      const wordmark = img.nextElementSibling as HTMLElement | null;
      if (wordmark) wordmark.style.display = "inline-block";
    },
  }) as HTMLImageElement;

  const wordmark = el(
    "span",
    { className: "dpdpa-wordmark", style: { display: "none" } },
    brand.name
  );

  return el("div", { className: "dpdpa-header" }, logoImg, wordmark);
}

function inputRow(
  brand: ResolvedBrand,
  opts: {
    field: keyof Fields;
    label: string;
    type: string;
    placeholder: string;
    value: string;
    error: string | undefined;
    autocomplete?: string;
    onInput: (value: string) => void;
    onBlur: () => void;
  }
): { row: HTMLElement; input: HTMLInputElement; errorSlot: HTMLElement } {
  const inputEl = el("input", {
    type: opts.type,
    className: "dpdpa-input",
    placeholder: opts.placeholder,
    value: opts.value,
    autocomplete: opts.autocomplete,
    "aria-label": opts.label,
    onInput: (ev: Event) => opts.onInput((ev.currentTarget as HTMLInputElement).value),
    onBlur: () => opts.onBlur(),
  }) as HTMLInputElement;

  const errorSlot = el(
    "div",
    {
      className: "dpdpa-field-error",
      role: "alert",
      style: { display: opts.error ? "block" : "none" },
    },
    opts.error || ""
  );

  const labelEl = el("label", { className: "dpdpa-label" }, opts.label);

  const row = el(
    "div",
    { className: "dpdpa-field" },
    labelEl,
    inputEl,
    errorSlot
  );

  return { row, input: inputEl, errorSlot };
}

function footer(brand: ResolvedBrand): HTMLElement {
  return el(
    "div",
    { className: "dpdpa-footer" },
    "Powered by ",
    el("strong", null, brand.name)
  );
}

export function renderIdle(
  brand: ResolvedBrand,
  fields: Fields,
  errors: FieldErrors,
  generalErrorText: string | null,
  handlers: IdleHandlers
): IdleRefs {
  const nameRow = inputRow(brand, {
    field: "company_name",
    label: "Company name",
    type: "text",
    placeholder: "Acme Inc",
    value: fields.company_name,
    error: errors.company_name,
    autocomplete: "organization",
    onInput: (v) => handlers.onInput("company_name", v),
    onBlur: () => handlers.onBlur("company_name"),
  });
  const domainRow = inputRow(brand, {
    field: "company_domain",
    label: "Company domain",
    type: "text",
    placeholder: "acme.com",
    value: fields.company_domain,
    error: errors.company_domain,
    autocomplete: "url",
    onInput: (v) => handlers.onInput("company_domain", v),
    onBlur: () => handlers.onBlur("company_domain"),
  });
  const emailRow = inputRow(brand, {
    field: "email",
    label: "Work email",
    type: "email",
    placeholder: "you@acme.com",
    value: fields.email,
    error: errors.email,
    autocomplete: "email",
    onInput: (v) => handlers.onInput("email", v),
    onBlur: () => handlers.onBlur("email"),
  });

  const generalError = el(
    "div",
    {
      className: "dpdpa-general-error",
      role: "alert",
      style: { display: generalErrorText ? "block" : "none" },
    },
    generalErrorText || ""
  );

  const submitBtn = el(
    "button",
    {
      type: "submit",
      className: "dpdpa-btn-primary",
    },
    "Get my free report"
  ) as HTMLButtonElement;

  const form = el(
    "form",
    {
      className: "dpdpa-form",
      novalidate: "true",
      onSubmit: (ev: Event) => {
        ev.preventDefault();
        handlers.onSubmit();
      },
    },
    el(
      "h2",
      { className: "dpdpa-heading" },
      "Get your free DPDPA compliance report"
    ),
    el(
      "p",
      { className: "dpdpa-subheading" },
      "We'll preview your DPDPA exposure here and email you the full report."
    ),
    generalError,
    nameRow.row,
    domainRow.row,
    emailRow.row,
    submitBtn
  );

  const card = el(
    "div",
    { className: "dpdpa-card" },
    header(brand),
    form,
    footer(brand)
  );

  return {
    root: card,
    fieldInputs: {
      company_name: nameRow.input,
      company_domain: domainRow.input,
      email: emailRow.input,
    },
    fieldErrors: {
      company_name: nameRow.errorSlot,
      company_domain: domainRow.errorSlot,
      email: emailRow.errorSlot,
    },
    submitBtn,
    generalError,
  };
}

export function renderSubmitting(
  brand: ResolvedBrand,
  fields: Fields
): HTMLElement {
  const refs = renderIdle(brand, fields, {}, null, {
    onInput: () => {},
    onBlur: () => {},
    onSubmit: () => {},
  });
  Object.values(refs.fieldInputs).forEach((inp) => {
    inp.disabled = true;
  });
  refs.submitBtn.disabled = true;
  refs.submitBtn.textContent = "";
  refs.submitBtn.appendChild(
    el("span", { className: "dpdpa-spinner dpdpa-spinner-inline" })
  );
  refs.submitBtn.appendChild(document.createTextNode(" Submitting…"));
  return refs.root;
}

function animatedDots(): HTMLElement {
  return el(
    "span",
    { className: "dpdpa-dots", "aria-hidden": "true" },
    el("span"),
    el("span"),
    el("span")
  );
}

function stepPulse(brand: ResolvedBrand): HTMLElement {
  return el("div", {
    className: "dpdpa-step-icon dpdpa-step-icon-active",
    style: { backgroundColor: brand.primary },
  });
}

type PollStatus = "pending" | "researching" | "analysis_complete";

export function renderPolling(
  brand: ResolvedBrand,
  opts: {
    companyName: string;
    subStatus: PollStatus;
    elapsedMs: number;
    pollTimeoutMs: number;
  }
): HTMLElement {
  const pct = Math.min(100, Math.round((opts.elapsedMs / opts.pollTimeoutMs) * 100));
  const progressFill = el("div", {
    className: "dpdpa-progress-fill",
    style: { width: `${pct}%` },
  });
  const progressBar = el(
    "div",
    {
      className: "dpdpa-progress",
      role: "progressbar",
      "aria-label": "Quick insights progress",
      "aria-valuemin": "0",
      "aria-valuemax": "100",
      "aria-valuenow": String(pct),
    },
    progressFill
  );

  const stepRow = el(
    "div",
    { className: "dpdpa-step dpdpa-step-active" },
    stepPulse(brand),
    el(
      "span",
      { className: "dpdpa-step-label dpdpa-shimmer" },
      `Scanning ${opts.companyName}'s privacy footprint`
    ),
    animatedDots()
  );

  const body = el(
    "div",
    { className: "dpdpa-polling" },
    el("div", { className: "dpdpa-steps" }, stepRow),
    el(
      "p",
      { className: "dpdpa-polling-sub" },
      "Generating your preliminary DPDPA scan. This usually takes 10–20 seconds."
    ),
    progressBar
  );

  return el("div", { className: "dpdpa-card" }, header(brand), body, footer(brand));
}

function chip(text: string, variant?: "ref" | "data" | "touchpoint"): HTMLElement {
  return el(
    "span",
    { className: `dpdpa-chip${variant ? ` dpdpa-chip-${variant}` : ""}` },
    text
  );
}

function applicabilityBadge(level: string, reason: string): HTMLElement {
  const upper = (level || "").toUpperCase();
  let cls = "dpdpa-badge-neutral";
  if (upper.includes("VERY_LIKELY")) cls = "dpdpa-badge-strong";
  else if (upper === "LIKELY") cls = "dpdpa-badge-warn";
  else if (upper === "POSSIBLY") cls = "dpdpa-badge-soft";
  else if (upper === "UNLIKELY") cls = "dpdpa-badge-mute";

  return el(
    "div",
    { className: `dpdpa-applicability ${cls}` },
    el("span", { className: "dpdpa-applicability-level" }, level.replace(/_/g, " ")),
    el("span", { className: "dpdpa-applicability-reason" }, reason)
  );
}

function sectionTitle(text: string): HTMLElement {
  return el("h4", { className: "dpdpa-section-title" }, text);
}

function areaCard(area: QuickInsights["areas_under_review"][number]): HTMLElement {
  const refs = el(
    "div",
    { className: "dpdpa-chip-row" },
    ...area.dpdpa_references.map((r) => chip(r, "ref"))
  );

  const hint = area.redacto_product_hint
    ? el(
        "span",
        { className: "dpdpa-product-hint" },
        `Redacto ${area.redacto_product_hint}`
      )
    : null;

  const heading = el(
    "div",
    { className: "dpdpa-area-heading" },
    el("h5", { className: "dpdpa-area-name" }, area.area),
    ...(hint ? [hint] : [])
  );

  return el(
    "div",
    { className: "dpdpa-area-card" },
    heading,
    refs,
    el("p", { className: "dpdpa-area-why" }, area.why_this_is_being_checked)
  );
}

function emailIcon(): SVGElement {
  return svg(
    "svg",
    { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none" },
    svg("path", {
      d: "M4 6h16v12H4z",
      stroke: "currentColor",
      "stroke-width": 2,
      "stroke-linejoin": "round",
    }),
    svg("path", {
      d: "M4 7l8 6 8-6",
      stroke: "currentColor",
      "stroke-width": 2,
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
    })
  );
}

export function renderQuickInsights(
  brand: ResolvedBrand,
  opts: {
    companyName: string;
    email: string;
    insights: QuickInsights;
    onReset: () => void;
  }
): HTMLElement {
  const ins = opts.insights;

  const title = el(
    "h3",
    { className: "dpdpa-qi-title" },
    `Preliminary DPDPA scan for ${opts.companyName}`
  );

  const subtitle = el(
    "p",
    { className: "dpdpa-qi-subtitle" },
    ins.summary
  );

  const confidence = el(
    "div",
    { className: "dpdpa-confidence-row" },
    el("span", { className: "dpdpa-meta-label" }, "Confidence"),
    chip(ins.confidence)
  );

  const applicability = applicabilityBadge(
    ins.dpdpa_applicability_signal.level,
    ins.dpdpa_applicability_signal.reason
  );

  const eduNote = el(
    "div",
    { className: "dpdpa-callout" },
    el("div", { className: "dpdpa-callout-title" }, ins.educational_note.title),
    el("p", { className: "dpdpa-callout-message" }, ins.educational_note.message)
  );

  const areasSection = el(
    "section",
    { className: "dpdpa-section" },
    sectionTitle("Areas under review"),
    el(
      "div",
      { className: "dpdpa-area-list" },
      ...ins.areas_under_review.map((a) => areaCard(a))
    )
  );

  const cu = ins.company_understanding;
  const companySection = el(
    "section",
    { className: "dpdpa-section" },
    sectionTitle("Company understanding"),
    el("p", { className: "dpdpa-cu-profile" }, cu.business_profile),
    el(
      "div",
      { className: "dpdpa-cu-meta" },
      el("span", { className: "dpdpa-meta-label" }, "Likely users"),
      el("span", { className: "dpdpa-meta-value" }, cu.likely_user_relationship)
    ),
    el(
      "div",
      { className: "dpdpa-chip-row" },
      ...cu.likely_digital_touchpoints.map((t) => chip(t, "touchpoint"))
    )
  );

  const dataSection = el(
    "section",
    { className: "dpdpa-section" },
    sectionTitle("Data likely to validate"),
    el(
      "div",
      { className: "dpdpa-chip-row" },
      ...ins.likely_data_to_validate.map((d) => chip(d, "data"))
    )
  );

  const emailBanner = el(
    "div",
    { className: "dpdpa-email-banner" },
    el("span", { className: "dpdpa-email-icon" }, emailIcon()),
    el(
      "div",
      { className: "dpdpa-email-text" },
      el(
        "strong",
        { className: "dpdpa-email-headline" },
        "Your full DPDPA report is on its way."
      ),
      el(
        "p",
        { className: "dpdpa-email-sub" },
        `We'll email it to `,
        el("strong", null, opts.email),
        ` once compliance validation finishes.`
      )
    )
  );

  const resetLink = el(
    "a",
    {
      href: "#",
      className: "dpdpa-reset-link",
      onClick: (ev: Event) => {
        ev.preventDefault();
        opts.onReset();
      },
    },
    "Run another scan"
  );

  const body = el(
    "div",
    { className: "dpdpa-qi" },
    el(
      "div",
      { className: "dpdpa-qi-header" },
      title,
      applicability
    ),
    subtitle,
    confidence,
    eduNote,
    areasSection,
    companySection,
    dataSection,
    emailBanner,
    resetLink
  );

  return el(
    "div",
    { className: "dpdpa-card dpdpa-card-wide" },
    header(brand),
    body,
    footer(brand)
  );
}

export function renderError(
  brand: ResolvedBrand,
  opts: {
    message: string;
    retryable: boolean;
    onRetry: () => void;
    onBack: () => void;
  }
): HTMLElement {
  const retryBtn = el(
    "button",
    {
      type: "button",
      className: "dpdpa-btn-primary",
      onClick: () => (opts.retryable ? opts.onRetry() : opts.onBack()),
    },
    opts.retryable ? "Try again" : "Edit details"
  );

  const body = el(
    "div",
    { className: "dpdpa-error-view" },
    el("h3", { className: "dpdpa-error-title" }, "Something went wrong"),
    el("p", { className: "dpdpa-error-message" }, opts.message),
    retryBtn
  );

  return el("div", { className: "dpdpa-card" }, header(brand), body, footer(brand));
}
